import { useEffect, useMemo, useRef, useState } from "react";
import { FONT, COLOR } from "../lib/tokens";
import { supabase } from "../lib/supabase";
import { attachReceiptToTrip } from "../lib/attachReceiptToTrip";
import { findIngredient } from "../data/ingredients";

// TripDetailModal — drill-down view of one committed shopping trip.
// Reachable from TripHistoryModal. Shows the trip's scanned items
// (from trip_scans, the in-session record), the receipt status, and
// the relevant action: view the receipt if one exists, otherwise
// attach one via attachReceiptToTrip.
//
// Items source — `trip_scans` is the truest record of what was in
// the cart during the session (every UPC scanned). pantry_items
// don't carry a trip_id so we can't reliably scope by that. If a
// receipt is attached, its `scan_items` JSONB also carries the
// per-line price snapshot from commit time — we read that and
// hydrate prices onto the matching trip_scans rows by barcode_upc.
//
// Props:
//   tripId            — shopping_trips.id
//   userId            — viewer (for ATTACH ownership)
//   onOpenReceipt(id) — parent opens ReceiptView
//   onClose()         — dismiss
//   onAttachComplete? — optional callback fired after a successful
//                       attach so parents can re-fetch their lists
export default function TripDetailModal({
  tripId,
  userId,
  onOpenReceipt,
  onClose,
  onAttachComplete,
}) {
  const [trip, setTrip] = useState(null);
  const [scans, setScans] = useState(null);
  const [receipt, setReceipt] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const onKey = (e) => { if (e.key === "Escape") onClose?.(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  useEffect(() => {
    let alive = true;
    if (!tripId) return;
    (async () => {
      setError(null);
      const [tripRes, scanRes] = await Promise.all([
        supabase
          .from("shopping_trips")
          .select("id, user_id, store_name, started_at, ended_at, receipt_id, status, created_at")
          .eq("id", tripId)
          .single(),
        supabase
          .from("trip_scans")
          .select("id, barcode_upc, status, canonical_id, brand, product_name, qty, scanned_at")
          .eq("trip_id", tripId)
          .order("scanned_at", { ascending: true }),
      ]);
      if (!alive) return;
      if (tripRes.error) {
        setError(tripRes.error.message);
        return;
      }
      setTrip(tripRes.data);
      setScans(scanRes.data || []);

      // If a receipt is linked, pull it for header info + per-line
      // prices. RLS scopes to viewer.
      if (tripRes.data?.receipt_id) {
        const { data: rcpt } = await supabase
          .from("receipts")
          .select("id, store_name, receipt_date, total_cents, item_count, image_path, scan_items, created_at")
          .eq("id", tripRes.data.receipt_id)
          .single();
        if (alive && rcpt) setReceipt(rcpt);
      } else if (alive) {
        setReceipt(null);
      }
    })();
    return () => { alive = false; };
  }, [tripId, refreshTick]);

  // Build a map: barcode_upc → priceCents from the receipt snapshot.
  // scan_items is the array commitTrip wrote at checkout; barcodes
  // there match trip_scans 1:1 since that's what the snapshot was
  // built from.
  const priceByUpc = useMemo(() => {
    const m = new Map();
    if (!receipt?.scan_items || !Array.isArray(receipt.scan_items)) return m;
    for (const item of receipt.scan_items) {
      if (item?.barcode && typeof item.priceCents === "number") {
        m.set(item.barcode, item.priceCents);
      }
    }
    return m;
  }, [receipt]);

  function startAttach() {
    fileInputRef.current?.click();
  }

  async function handleFileChosen(e) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !trip) return;
    setBusy(true);
    setError(null);
    try {
      const result = await attachReceiptToTrip({
        file,
        tripId: trip.id,
        userId,
        supabase,
        itemCount: scans?.length || null,
      });
      if (!result.ok) {
        setError(result.error || "Couldn't attach that receipt.");
      } else {
        onAttachComplete?.();
        setRefreshTick((n) => n + 1);
      }
    } finally {
      setBusy(false);
    }
  }

  const dateLabel = (() => {
    const a = trip?.ended_at || trip?.created_at;
    if (!a) return "";
    return new Date(a).toLocaleDateString(undefined, {
      weekday: "short", month: "short", day: "numeric", year: "numeric",
    });
  })();

  const totalLabel = receipt && typeof receipt.total_cents === "number"
    ? `$${(receipt.total_cents / 100).toFixed(2)}`
    : null;

  const greenCount = useMemo(() => {
    if (!scans) return 0;
    return scans.filter((s) => s.status === "green").length;
  }, [scans]);

  const totalQty = useMemo(() => {
    if (!scans) return 0;
    return scans.reduce((n, s) => n + (s.qty || 1), 0);
  }, [scans]);

  return (
    <div style={{
      position: "fixed", inset: 0, background: "#000000dd", zIndex: 317,
      display: "flex", alignItems: "flex-end",
      maxWidth: 480, margin: "0 auto",
    }}>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        capture="environment"
        onChange={handleFileChosen}
        style={{ display: "none" }}
      />

      <div style={{
        width: "100%", background: COLOR.ground,
        borderRadius: "20px 20px 0 0", padding: "18px 22px 36px",
        maxHeight: "92vh", overflowY: "auto",
        position: "relative",
      }}>
        <div style={{ width: 44, height: 4, background: COLOR.border, borderRadius: 2, margin: "0 auto 14px" }} />

        <button
          onClick={onClose}
          aria-label="Close"
          style={{
            position: "absolute", top: 12, right: 14,
            width: 32, height: 32,
            background: COLOR.deep, border: `1px solid ${COLOR.border}`,
            color: COLOR.dim, borderRadius: 16,
            fontFamily: FONT.mono, fontSize: 14,
            cursor: "pointer", zIndex: 1,
          }}
        >
          ✕
        </button>

        <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.leaf, letterSpacing: "0.12em", marginBottom: 6 }}>
          TRIP DETAIL
        </div>
        <h2 style={{ fontFamily: FONT.serif, fontSize: 22, fontStyle: "italic", color: COLOR.ink, fontWeight: 500, margin: 0, lineHeight: 1.2, marginBottom: 4 }}>
          {trip?.store_name || receipt?.store_name || "Unknown store"}
        </h2>
        {dateLabel && (
          <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.sub, letterSpacing: "0.08em", marginBottom: 14 }}>
            {dateLabel.toUpperCase()}
          </div>
        )}

        {error && (
          <div style={{
            padding: "10px 12px", marginBottom: 12,
            background: "#3a1a1a", border: `1px solid ${COLOR.border}`, borderRadius: 8,
            fontFamily: FONT.mono, fontSize: 11, color: "#ff9b9b",
          }}>
            {error}
          </div>
        )}

        {!trip && !error && (
          <div style={{ padding: "40px 12px", textAlign: "center", fontFamily: FONT.mono, fontSize: 11, color: COLOR.sub, letterSpacing: "0.1em" }}>
            LOADING…
          </div>
        )}

        {trip && (
          <>
            {/* Summary strip — counts + total */}
            <div style={{
              display: "grid", gridTemplateColumns: "1fr 1fr 1fr",
              gap: 8, marginBottom: 14,
            }}>
              <SummaryTile label="ITEMS" value={String(totalQty)} />
              <SummaryTile label="RESOLVED" value={`${greenCount}/${scans?.length || 0}`} />
              <SummaryTile label="TOTAL" value={totalLabel || "—"} />
            </div>

            {/* Receipt action */}
            <div style={{ marginBottom: 18 }}>
              {trip.receipt_id ? (
                <button
                  onClick={() => onOpenReceipt?.(trip.receipt_id)}
                  style={{
                    display: "block", width: "100%",
                    padding: "12px 14px",
                    background: COLOR.deep, border: `1px solid ${COLOR.edge}`,
                    borderRadius: 10, cursor: "pointer", textAlign: "left",
                    touchAction: "manipulation",
                  }}
                >
                  <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.leafMuted, letterSpacing: "0.12em", marginBottom: 4 }}>
                    RECEIPT ATTACHED
                  </div>
                  <div style={{ fontFamily: FONT.serif, fontSize: 14, fontStyle: "italic", color: COLOR.ink }}>
                    View receipt photo + line items →
                  </div>
                </button>
              ) : (
                <button
                  onClick={startAttach}
                  disabled={busy}
                  style={{
                    display: "block", width: "100%",
                    padding: "12px 14px",
                    background: "#2a3a1f", border: `1px solid ${COLOR.leaf}`,
                    borderRadius: 10,
                    cursor: busy ? "default" : "pointer",
                    textAlign: "left",
                    opacity: busy ? 0.7 : 1,
                    touchAction: "manipulation",
                  }}
                >
                  <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.leaf, letterSpacing: "0.12em", marginBottom: 4 }}>
                    {busy ? "ATTACHING…" : "NO RECEIPT"}
                  </div>
                  <div style={{ fontFamily: FONT.serif, fontSize: 14, fontStyle: "italic", color: COLOR.ink }}>
                    {busy ? "Reading photo + uploading…" : "Attach a receipt photo →"}
                  </div>
                </button>
              )}
            </div>

            {/* Items list */}
            <div style={{ fontFamily: FONT.mono, fontSize: 10, color: COLOR.sky, letterSpacing: "0.12em", marginBottom: 8 }}>
              ITEMS SCANNED
            </div>
            {scans && scans.length === 0 && (
              <div style={{
                padding: "20px 14px", textAlign: "center",
                background: COLOR.deep, border: `1px dashed ${COLOR.edge}`, borderRadius: 10,
                fontFamily: FONT.sans, fontSize: 12, color: COLOR.muted, fontStyle: "italic",
              }}>
                No items scanned during this trip.
              </div>
            )}
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {scans?.map((s) => {
                const ing = s.canonical_id ? findIngredient(s.canonical_id) : null;
                const name = s.product_name || ing?.name || s.brand || s.barcode_upc;
                const priceCents = priceByUpc.get(s.barcode_upc);
                const tone = s.status === "green" ? COLOR.leaf
                  : s.status === "yellow" ? "#d4a83a"
                  : "#c46868";
                return (
                  <div
                    key={s.id}
                    style={{
                      display: "flex", alignItems: "center", gap: 10,
                      padding: "10px 12px",
                      background: COLOR.ground, border: `1px solid ${COLOR.edge}`,
                      borderRadius: 10,
                    }}
                  >
                    <div style={{
                      width: 6, height: 6, borderRadius: 3,
                      background: tone, flexShrink: 0,
                    }} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontFamily: FONT.serif, fontSize: 13, color: COLOR.ink, fontStyle: "italic", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                        {name || "Unknown item"}
                      </div>
                      <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.sub, marginTop: 2, letterSpacing: "0.05em" }}>
                        {s.brand ? `${s.brand.toUpperCase()} · ` : ""}
                        {s.qty > 1 ? `×${s.qty} · ` : ""}
                        {s.barcode_upc ? `UPC ${s.barcode_upc.slice(-6)}` : ""}
                      </div>
                    </div>
                    {typeof priceCents === "number" && (
                      <span style={{ fontFamily: FONT.mono, fontSize: 12, color: COLOR.leafMuted, letterSpacing: "0.04em" }}>
                        ${(priceCents / 100).toFixed(2)}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SummaryTile({ label, value }) {
  return (
    <div style={{
      padding: "10px 8px",
      background: COLOR.deep,
      border: `1px solid ${COLOR.edge}`,
      borderRadius: 8,
      textAlign: "center",
    }}>
      <div style={{ fontFamily: FONT.mono, fontSize: 9, color: COLOR.sub, letterSpacing: "0.12em", marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontFamily: FONT.serif, fontSize: 16, fontStyle: "italic", color: COLOR.ink }}>
        {value}
      </div>
    </div>
  );
}
